














































































































import { computed, defineComponent, ref } from '@nuxtjs/composition-api';
import type { PropType } from '@nuxtjs/composition-api';
import { useResizeObserver } from '@vueuse/core';
import { useAnchorNavigation, useUiMode, useIntegrations } from '~/composables';
import TwicpicsImage from '~/components/atoms/TwicpicsImage/TwicpicsImage.vue';
import ComparisonProductDetails from
  '~/components/molecules/Product/ComparisonProducts/ComparisonProductDetails/ComparisonProductDetails.vue';
import type { ComparisonProductData, AttributesArray } from '~/types/product/ComparisonProducts';
import Link from '~/components/atoms/Link/Link.vue';
import { LinkType } from '~/types/components/Link/LinkType';
import { CLICK_PRODUCT_IN_COMPARISON_TABLE } from '~/constants/googleTagManager';
import { ProductVariantExtended } from '~/types/product/ProductVariantExtended';
import { getProductIsOnStock } from '~/helpers/product/getProductStockInfo/getProductStockInfo';
import { DEFAULT_ATTRIBUTE_LIMIT } from '~/constants/comparisonProducts';
import ComparisonTableRow from
  '~/components/molecules/Product/ComparisonProducts/ComparisonTableRow/ComparisonTableRow.vue';

export default defineComponent({
  name: 'ComparisonTable',
  components: {
    TwicpicsImage,
    ComparisonProductDetails,
    Link,
    ComparisonTableRow,
  },
  props: {
    productsData: {
      type: Array as PropType<ComparisonProductData[]>,
      required: true,
    },
    attributes: {
      type: Array as PropType<AttributesArray>,
      required: true,
    },
    currentSku: {
      type: String,
      required: true,
    },
    showMore: {
      type: Boolean,
      required: true,
    },
  },
  setup() {
    const { isMobile } = useUiMode();
    const { stickyAnchorNavHeight } = useAnchorNavigation();
    const { $tagManager } = useIntegrations();

    const tableContainer = ref(null);
    const tableElement = ref(null);
    const isDragging = ref(false);
    const tablePosition = ref(0);
    const startPosition = ref(0);
    const scrollSpace = ref(0);

    const setScrollSpace = (): void => {
      if (!tableElement.value || !tableContainer.value) {
        return;
      }
      scrollSpace.value = tableElement.value.offsetWidth - tableContainer.value.offsetWidth;
    };
    const setStartPosition = (eventPosition: number): void => {
      startPosition.value = tablePosition.value + eventPosition;
    };
    const setCurrentPosition = (eventPosition: number): void => {
      const newPosition = startPosition.value - eventPosition;
      if (newPosition < 0) {
        tablePosition.value = 0;
        return;
      }
      if (newPosition > scrollSpace.value) {
        tablePosition.value = scrollSpace.value;
        return;
      }
      tablePosition.value = newPosition;
    };

    const tablePositionTop = computed(() => {
      return { top: `${isMobile.value ? 0 : stickyAnchorNavHeight.value}px` };
    });
    const tablePositionLeft = computed(() => {
      return { left: `${-tablePosition.value}px` };
    });
    const tablePositionLeftReverse = computed(() => {
      return { left: `${tablePosition.value}px` };
    });

    const resizeCallback = (): void => {
      setScrollSpace();
      setStartPosition(0);
      setCurrentPosition(0);
    };

    useResizeObserver(tableContainer, resizeCallback);

    const onMouseDown = (event: MouseEvent): void => {
      isDragging.value = true;
      setStartPosition(event.clientX);
    };
    const onMouseMove = (event: MouseEvent): void => {
      if (isDragging.value) {
        setCurrentPosition(event.clientX);
      }
    };
    const onMouseUp = (): void => {
      isDragging.value = false;
    };

    const onTouchStart = (event: TouchEvent): void => {
      isDragging.value = true;
      setStartPosition(event.touches[0].clientX);
    };
    const onTouchMove = (event: TouchEvent): void => {
      if (isDragging.value) {
        setCurrentPosition(event.touches[0].clientX);
      }
    };
    const onTouchEnd = (): void => {
      isDragging.value = false;
    };
    const onClick = (product: ProductVariantExtended) => {
      $tagManager.events.triggerViewProductTags({
        product,
        inStock: getProductIsOnStock(product),
        eventName: CLICK_PRODUCT_IN_COMPARISON_TABLE,
      });
    };

    return {
      tableContainer,
      tableElement,
      isDragging,
      tablePositionTop,
      tablePositionLeft,
      tablePositionLeftReverse,
      startPosition,
      tablePosition,
      scrollSpace,
      onMouseDown,
      onMouseUp,
      onMouseMove,
      onTouchStart,
      onTouchEnd,
      onTouchMove,
      LinkType,
      onClick,
      getProductIsOnStock,
      DEFAULT_ATTRIBUTE_LIMIT,
    };
  },
});

